import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseKey) {
  throw new Error(
    'Missing Supabase environment variables. Please check your .env file.\n' +
    'Required: VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY'
  )
}

// BroadcastChannel syncs sessionStorage across open tabs so a new tab
// inherits the existing session without falling back to localStorage.
const channel = 'BroadcastChannel' in window
  ? new BroadcastChannel('triptic-auth-sync')
  : null

const sharedSessionStorage = {
  getItem: (key: string): string | null => {
    return sessionStorage.getItem(key)
  },
  setItem: (key: string, value: string): void => {
    sessionStorage.setItem(key, value)
    channel?.postMessage({ type: 'set', key, value })
  },
  removeItem: (key: string): void => {
    sessionStorage.removeItem(key)
    if (key.startsWith('sb-')) {
      channel?.postMessage({ type: 'remove', key })
    }
  },
}

// Listen for changes from other tabs and sync into this tab's sessionStorage
if (channel) {
  channel.onmessage = (e) => {
    if (e.data?.type === 'set' && e.data.key && e.data.value) {
      sessionStorage.setItem(e.data.key, e.data.value)
    } else if (e.data?.type === 'remove' && e.data.key) {
      if (e.data.key.startsWith('sb-')) {
        sessionStorage.removeItem(e.data.key)
      }
    }
  }

  // On tab open, ask other tabs for their current session
  channel.postMessage({ type: 'request-session' })
}

export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    storage: sharedSessionStorage,
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
  }
})

// When a tab requests session, all other tabs broadcast their sb-* keys
if (channel) {
  channel.addEventListener('message', (e) => {
    if (e.data?.type === 'request-session') {
      Object.keys(sessionStorage)
        .filter(k => k.startsWith('sb-'))
        .forEach(k => {
          const value = sessionStorage.getItem(k)
          if (value) channel.postMessage({ type: 'set', key: k, value })
        })
    }
  })
}
